import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import styles from './JobDetailNew.module.css'; 
import { API_BASE_URL } from '../../config';
import axios from 'axios';
import { 
  ArrowLeft, MapPin, Calendar, Edit2, Users, 
  MessageSquare, UserPlus, CheckCircle, Globe, Lock,
  Camera, X, ChevronLeft, ChevronRight,
  Briefcase, Star, Info
} from "lucide-react";
import Loader2 from '../../components/loader2';
import showToast from '../../components/ShowToast';

// --- IMPORTS ---
import BusinessProfilePureCSS from '../../profiles/BusinessProfilePureCSS'; 
import BrowseProsModal from '../../components/Projects/Modals/BrowseProsModalUpdated';
import ModernNavbarPureCSS from "../../components/Navbar/ModernNavBarPureCSS";
import { jwtDecode } from 'jwt-decode';


const JobManagementPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { jobId } = useParams();
  
  // --- STATE ---
  const [activeTab, setActiveTab] = useState('details'); 
  const [jobData, setJobData] = useState(null);
  const [leads, setLeads] = useState([]); 
  const [quotes, setQuotes] = useState([]); 
  const [isLoading, setIsLoading] = useState(true);
  
  // Image Modal State
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Business Profile Panel State
  const [selectedBusinessId, setSelectedBusinessId] = useState(null);
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  // Browse Pros Modal State
  const [isBrowseProsOpen, setIsBrowseProsOpen] = useState(false);

  // Quote/Assign Modal State
  const [selectedQuote, setSelectedQuote] = useState(null); 
  const [isAssigning, setIsAssigning] = useState(false); 

      const [error, setError] = useState(null);
      const [username, setUsername] = useState('');
      const [homeUserId, setHomeUserId] = useState(null);
  


    useEffect(() => {
      const token = localStorage.getItem('token');
      let uid = null;
      let uname = '';
      if (token) {
        try {
          const decodedToken = jwtDecode(token);
          uname = decodedToken.UserName || localStorage.getItem('loggedInUsername') || 'User';
          uid = decodedToken.nameid || localStorage.getItem('loggedInHomeUserId');
        } catch (e) {
          console.warn("JWT decode failed, falling back to storage:", e);
          uname = localStorage.getItem('loggedInUsername') || 'User';
          uid = localStorage.getItem('loggedInHomeUserId');
        }
      } else {
        uname = localStorage.getItem('loggedInUsername') || '';
        uid = localStorage.getItem('loggedInHomeUserId');
      }
      if (uname) setUsername(uname);
      if (uid) setHomeUserId(uid);
      if (!uid) setError("No user identified. Please log in.");
    }, []);


  // --- INITIALIZATION ---
  useEffect(() => {
    fetchData();
  }, [jobId]);

  // Handle auto-opening invite modal if redirected from Wizard
  useEffect(() => {
      if (location.state?.openInviteModal) {
          setIsBrowseProsOpen(true);
          // Clear state so it doesn't reopen on refresh
          window.history.replaceState({}, document.title);
      }
  }, [location.state]);

  const fetchData = async () => {
    try {
        const token = localStorage.getItem('token');
        let userId = 0;
        let userType = "HomeUser";
        if(token) {
             const rawType = localStorage.getItem('userType');
             userId = rawType === 'Home' ? parseInt(localStorage.getItem('loggedInHomeUserId')) : parseInt(localStorage.getItem('loggedInBusinessId'));
        }

        // 1. Fetch Job Details
        const jobRes = await axios.get(`${API_BASE_URL}/api/Jobs/getjobbyid?id=${jobId}`);
        setJobData(jobRes.data);

        // 2. Fetch Quotes
        const quotesRes = await axios.get(`${API_BASE_URL}/api/Jobs/quote/${jobId}`).catch(()=>({data:[]}));
        setQuotes(quotesRes.data || []);

        // 3. Fetch Invites (Leads)
        const leadsRes = await axios.get(`${API_BASE_URL}/api/Jobs/getjobleads?userId=${userId}&userType=${userType}`);
        const allLeads = Array.isArray(leadsRes.data) ? leadsRes.data : (leadsRes.data?.result || []);
        // Filter leads for this specific job
        setLeads(allLeads.filter(l => String(l.jobId || l.JobId) === String(jobId)));

    } catch (err) {
        console.error("Error fetching job data", err);
    } finally {
        setIsLoading(false);
    }
  };

  // --- HANDLERS ---

  const openBusinessProfile = (bizId) => {
      if (!bizId) return;
      setSelectedBusinessId(bizId);
      setIsProfileOpen(true);
  };

  const handleInviteMore = () => {
      setIsBrowseProsOpen(true);
  };

  const handleAssignJob = async () => {
      if (!selectedQuote || !selectedQuote.business?.businessId) return;
      
      if(!window.confirm(`Are you sure you want to hire ${selectedQuote.business.businessName}? This will notify them.`)) return;

      setIsAssigning(true);
      try {
          await axios.post(`${API_BASE_URL}/api/Jobs/${jobId}/hire/${selectedQuote.business.businessId}`);
          showToast('success', `Job Assigned to ${selectedQuote.business.businessName}`);
          setSelectedQuote(null); // Close modal
          fetchData(); // Refresh to show assigned status
      } catch (err) {
          showToast('error', "Failed to assign job.");
          console.error(err);
      } finally {
          setIsAssigning(false);
      }
  };

  const handleMarkComplete = async () => {
      if(!window.confirm("Mark this job as completed? This will close the task.")) return;
      try {
          await axios.patch(`${API_BASE_URL}/api/Jobs/updatestatus/${jobId}`, { Status: "Completed" });
          showToast('success', "Job marked as Completed!");
          fetchData();
      } catch (err) {
          showToast('error', "Failed to update status.");
      }
  };

  const getJobImages = () => {
    if (!jobData) return [];
    return [jobData.image1, jobData.image2, jobData.image3, jobData.image4].filter(Boolean);
  };
  const images = getJobImages();

  if (isLoading) return <Loader2 />;
  if (!jobData) return <div>Job not found</div>;

  // Status Logic
  const isPublic = jobData.isPublish; 
  const isAssigned = jobData.jobStatus === "Assigned" || jobData.jobStatus === "In Progress";
  const isCompleted = jobData.jobStatus === "Completed";

  return (


    
    <div style={{minHeight:'100vh', background:'#F8FAFC'}}>

    <ModernNavbarPureCSS UserName={username} prefix={''} TabActiveId={'projects'} />
      
      {/* 1. HEADER */}
      <header className={styles.jobDetailNew_header} style={{paddingBottom:'3rem'}}>
        <div className={styles.jobDetailNew_container}>
          <button onClick={() => navigate(-1)} style={{color:'white', background:'none', border:'none', cursor:'pointer', display:'flex', alignItems:'center', gap:'10px', marginBottom:'1rem'}}>
             <ArrowLeft /> Back to Project
          </button>
          
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start'}}>
            <div>
                <div style={{display:'flex', alignItems:'center', gap:'10px', marginBottom:'0.5rem'}}>
                    {isPublic ? 
                        <span className={styles.badgeBlue}><Globe size={12}/> Public Broadcast</span> : 
                        <span className={styles.badgeGray}><Lock size={12}/> Invite Only</span>
                    }
                    <span className={isCompleted ? styles.badgeGreen : isAssigned ? styles.badgeOrange : styles.badgeGreen}>
                        {jobData.jobStatus}
                    </span>
                </div>
                <h1 style={{fontSize:'2rem', fontWeight:'bold', color:'white', margin:0}}>{jobData.name}</h1>
                <div style={{color:'#BFDBFE', marginTop:'0.5rem', display:'flex', gap:'1rem', alignItems:'center'}}>
                    <span style={{display:'flex', gap:'5px', alignItems:'center'}}><MapPin size={16}/> {jobData.postcode}</span>
                </div>
            </div>

            <div style={{display:'flex', gap:'10px'}}>
                {!isCompleted && !isAssigned && (
                    <button onClick={() => navigate(`/job-detail-new/${jobId}`)} className={styles.btnHeaderOutline}>
                        <Edit2 size={18}/> Edit Job
                    </button>
                )}
                {!isCompleted && (
                    <button onClick={handleInviteMore} className={styles.btnHeaderPrimary}>
                        <UserPlus size={18}/> {isPublic ? 'Boost: Invite Top Pros' : 'Invite Professionals'}
                    </button>
                )}
            </div>
          </div>
        </div>
      </header>

      {/* 2. CONSOLE CONTENT CARD */}
      <div className={styles.jobDetailNew_container} style={{marginTop:'-2.5rem'}}>
         <div className={styles.consoleCard}>
            
            {/* TABS */}
            <div className={styles.consoleTabs}>
                <button onClick={() => setActiveTab('details')} className={activeTab === 'details' ? styles.tabActive : styles.tabInactive}>
                    Job Details
                </button>
                <button onClick={() => setActiveTab('quotes')} className={activeTab === 'quotes' ? styles.tabActive : styles.tabInactive}>
                    Quotes Received ({quotes.length})
                </button>
                <button onClick={() => setActiveTab('invites')} className={activeTab === 'invites' ? styles.tabActive : styles.tabInactive}>
                    Invited Pros ({leads.length})
                </button>
            </div>

            <div style={{padding:'2rem'}}>
                
                {/* --- TAB: DETAILS --- */}
                {activeTab === 'details' && (
                    <div className={styles.detailsGrid}>
                        <div className={styles.leftCol}>
                            {/* Description */}
                            <div className={styles.sectionBlock}>
                                <h3 className={styles.sectionTitle}>Description</h3>
                                <p className={styles.descText}>{jobData.description}</p>
                            </div>

                            {/* Stats Row */}
                            <div className={styles.statsRow}>
                                <div className={styles.statBox}>
                                    <span className={styles.statLabel}>Budget</span>
                                    <span className={styles.statValueGreen}>
                                        {jobData.budgetAmount ? `$${jobData.budgetAmount}` : jobData.budget}
                                    </span>
                                </div>
                                <div className={styles.statBox}>
                                    <span className={styles.statLabel}>Trade</span>
                                    <span className={styles.statValue}>{jobData.profession?.[0]?.name || "General"}</span>
                                </div>
                                <div className={styles.statBox}>
                                    <span className={styles.statLabel}>Due Date</span>
                                    <span className={styles.statValue}>{jobData.deadline}</span>
                                </div>
                            </div>

                            {/* Images */}
                            {images.length > 0 && (
                                <div className={styles.sectionBlock} style={{marginTop:'2rem'}}>
                                    <h3 className={styles.sectionTitle}><Camera size={16}/> Project Photos</h3>
                                    <div className={styles.imageGrid}>
                                        {images.map((img, idx) => (
                                            <div key={idx} className={styles.thumbWrapper} onClick={() => {setCurrentImageIndex(idx); setIsImageModalOpen(true)}}>
                                                <img src={img} alt="Job" />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Right Col: Management */}
                        <div className={styles.rightCol}>
                            {(isAssigned || isCompleted) && jobData.assignedBusinessId ? (
                                <div className={styles.assignedCard}>
                                    <div className={styles.assignedHeader}>
                                        <CheckCircle size={20}/> Job Assigned
                                    </div>
                                    <div className={styles.assignedBody}>
                                        <h4>{jobData.businessName || "Contractor"}</h4>
                                        <p style={{fontSize:'0.85rem', color:'#64748B'}}>Assigned on {new Date(jobData.updated_at).toLocaleDateString()}</p>
                                        
                                        <button 
                                            onClick={() => openBusinessProfile(jobData.assignedBusinessId)} 
                                            className={styles.btnSmallOutline}
                                            style={{width: '100%', marginTop:'10px'}}
                                        >
                                            <Info size={16} style={{marginRight:'5px'}}/> View Profile
                                        </button>

                                        {!isCompleted && (
                                            <button onClick={handleMarkComplete} className={styles.btnMarkComplete}>
                                                Mark Job Complete
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className={styles.tipsCard}>
                                    <h4>Waiting for Quotes?</h4>
                                    <p>Inviting specific professionals increases your chance of a response by 80%.</p>
                                    <button onClick={handleInviteMore} className={styles.btnLink}>Browse Local Pros</button>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* --- TAB: QUOTES --- */}
                {activeTab === 'quotes' && (
                    <div className={styles.quotesContainer}>
                        {quotes.length === 0 ? (
                            <div className={styles.emptyState}>No quotes received yet.</div>
                        ) : (
                            quotes.map(quote => (
                                <div key={quote.id} className={styles.quoteCard}>
                                    <div className={styles.quoteLeft}>
                                        <div className={styles.quoteAvatar}>
                                            {quote.Business?.businessName?.charAt(0) || "B"}
                                        </div>
                                        <div>
                                            <h4 className={styles.quoteBizName}>{quote.Business?.businessName}</h4>
                                            <p className={styles.quoteMeta}>{quote.message ? quote.message.substring(0, 60) + "..." : "No message included"}</p>
                                        </div>
                                    </div>
                                    <div className={styles.quoteRight}>
                                        <div className={styles.quotePrice}>${quote.price}</div>
                                        <div style={{display:'flex', gap:'10px', justifyContent:'flex-end'}}>
                                            <button 
                                                onClick={() => openBusinessProfile(quote.Business?.businessId)}
                                                className={styles.btnSmallOutline}
                                            >
                                                Profile
                                            </button>
                                            <button onClick={() => setSelectedQuote(quote)} className={styles.btnViewQuote}>
                                                View Quote
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}

                {/* --- TAB: INVITES --- */}
                {activeTab === 'invites' && (
                    <div className={styles.invitesGrid}>
                        {leads.map((lead, idx) => (
                            <div key={idx} className={styles.inviteCard}>
                                <div className={styles.inviteHeader}>
                                    <div className={styles.inviteAvatar}><Briefcase size={20}/></div>
                                    <span className={styles.inviteStatus}>{lead.status || "Invited"}</span>
                                </div>
                                <h4 style={{marginTop:'10px'}}>Business ID: {lead.businessId}</h4>
                                
                                <button 
                                    onClick={() => openBusinessProfile(lead.businessId)} 
                                    className={styles.btnSmallOutline} 
                                    style={{width:'100%', marginTop:'10px'}}
                                >
                                    View Profile
                                </button>
                            </div>
                        ))}
                        {leads.length === 0 && <div className={styles.emptyState}>No professionals invited.</div>}
                    </div>
                )}

            </div>
         </div>
      </div>

      {/* --- MODALS --- */}

      {/* 1. IMAGE VIEWER */}
      {isImageModalOpen && images.length > 0 && (
        <div className={styles.imageViewerOverlay} onClick={() => setIsImageModalOpen(false)}>
            <button className={styles.closeBtn}><X size={32}/></button>
            <button className={styles.navBtnLeft} onClick={(e) => {e.stopPropagation(); setCurrentImageIndex((currentImageIndex - 1 + images.length) % images.length)}}><ChevronLeft size={40}/></button>
            <img src={images[currentImageIndex]} className={styles.zoomedImage} alt="Zoomed" onClick={(e) => e.stopPropagation()} />
            <button className={styles.navBtnRight} onClick={(e) => {e.stopPropagation(); setCurrentImageIndex((currentImageIndex + 1) % images.length)}}><ChevronRight size={40}/></button>
        </div>
      )}

      {/* 2. QUOTE DETAILS (With Assign Logic) */}
      {selectedQuote && (
          <div className={styles.modalOverlay} onClick={() => setSelectedQuote(null)}>
              <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
                  <div className={styles.modalHeader}>
                      <h2>Quote from {selectedQuote.business?.businessName}</h2>
                      <button onClick={() => setSelectedQuote(null)}><X size={20}/></button>
                  </div>
                  <div className={styles.modalBody}>
                      <div className={styles.quoteBigPrice}>${selectedQuote.price}</div>
                      
                      <div className={styles.quoteDetailRow}>
                          <Calendar size={16} /> <span>Received on {new Date(selectedQuote.created_at).toLocaleDateString()}</span>
                      </div>
                      
                      <div className={styles.messageBox}>
                          <label>Message</label>
                          <p>{selectedQuote.message || "No message provided."}</p>
                      </div>

                      <div className={styles.modalActions}>
                          <button 
                            onClick={() => openBusinessProfile(selectedQuote.business?.businessId)} 
                            className={styles.btnSecondary}
                          >
                            <Info size={18}/> View Profile
                          </button>
                          
                          {!isAssigned && !isCompleted && (
                              <button 
                                onClick={handleAssignJob} 
                                className={styles.btnPrimary} 
                                disabled={isAssigning}
                              >
                                {isAssigning ? 'Processing...' : 'Accept & Assign'}
                              </button>
                          )}
                      </div>
                  </div>
              </div>
          </div>
      )}

      {/* 3. BROWSE PROS */}
      <BrowseProsModal 
          isOpen={isBrowseProsOpen}
          onClose={() => setIsBrowseProsOpen(false)}
          taskId={jobData?.taskId} 
          professionId={jobData?.profession?.[0]?.id} 
          trade={jobData?.profession?.[0]?.name}
          postcode={jobData?.postcode}
          onInviteSent={() => fetchData()} 
      />

      {/* 4. BUSINESS PROFILE PANEL */}
      <BusinessProfilePureCSS
          isOpen={isProfileOpen}
          onClose={() => setIsProfileOpen(false)}
          businessId={selectedBusinessId}
          useIDTYPE={"BID"}
      />

    </div>
  );
};

export default JobManagementPage;